import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spin, Modal } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getUserRole } from "../../hooks/getUserRoles";
import { useGate } from "../Subscription/entitlements";
import {
  listReports,
  listTemplates,
  deleteReport,
  deleteTemplate,
  exportReport,
  canAnalyst,
  canDelete,
  type ReportListItem,
  type ReportTemplate,
} from "./reporting.service";
import "./Reporting.css";

type RowStatus = "draft" | "final" | "template";

interface Row {
  key: string;
  id: string;
  name: string;
  status: RowStatus;
  kind: "report" | "template";
  updatedAt: string;
}

const PAGE_SIZE = 10;

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
  </svg>
);

const ReportingPage: React.FC = () => {
  const { t } = useTranslation("reporting");
  const gate = useGate();
  const { id: projectId = "" } = useParams();
  const navigate = useNavigate();
  const role = getUserRole();
  const isAnalyst = canAnalyst(role);
  const isAdmin = canDelete(role);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [reports, templates] = await Promise.all([
        listReports(projectId).catch(() => [] as ReportListItem[]),
        listTemplates(projectId).catch(() => [] as ReportTemplate[]),
      ]);

      const reportRows: Row[] = reports.map((r) => ({
        key: `report-${r.id}`,
        id: r.id,
        name: r.name,
        status: r.status,
        kind: "report",
        updatedAt: r.generated_at || r.created_at,
      }));
      const templateRows: Row[] = templates.map((t) => ({
        key: `template-${t.id}`,
        id: t.id,
        name: t.name,
        status: "template",
        kind: "template",
        updatedAt: t.updated_at || t.created_at,
      }));

      const merged = [...reportRows, ...templateRows].sort((a, b) =>
        dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf(),
      );
      setRows(merged);
      setSelected(new Set());
      setPage((p) => Math.min(p, Math.max(1, Math.ceil(merged.length / PAGE_SIZE))));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t("reportingPage.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [rows, page],
  );

  const openBuilder = (row?: Row) => {
    if (!row) {
      navigate(`/reporting/${projectId}/builder`);
      return;
    }
    if (row.kind === "report") {
      navigate(`/reporting/${projectId}/builder/${row.id}`);
    } else {
      navigate(`/reporting/${projectId}/builder?templateId=${row.id}`);
    }
  };

  const handleExport = (row: Row) => {
    if (row.kind !== "report") return;
    // Export is a paid feature — on the Free plan this opens the Upgrade prompt.
    gate.requirePaid(async () => {
      try {
        await exportReport(row.id, "pdf", row.name);
      } catch (e: any) {
        toast.error(e?.response?.data?.detail || t("reportingPage.exportFailed"));
      }
    }, t("reportingPage.exportingReports"));
  };

  const handleDelete = (row: Row) => {
    Modal.confirm({
      title: row.kind === "report" ? t("reportingPage.deleteConfirmTitleReport") : t("reportingPage.deleteConfirmTitleTemplate"),
      content: t("reportingPage.deleteConfirmContent", { name: row.name }),
      okText: t("reportingPage.delete"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (row.kind === "report") await deleteReport(row.id);
          else await deleteTemplate(row.id);
          toast.success(t("reportingPage.deleteSuccess"));
          load();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || t("reportingPage.deleteFailed"));
        }
      },
    });
  };

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r.key));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageRows.forEach((r) => next.delete(r.key));
      else pageRows.forEach((r) => next.add(r.key));
      return next;
    });
  };

  return (
    <div className="reporting-page">
      <div className="reporting-card">
        <div className="reporting-header">
          <div>
            <h1 className="reporting-title">{t("reportingPage.title")}</h1>
            <p className="reporting-subtitle">
              {t("reportingPage.subtitle")}
            </p>
          </div>
          {isAnalyst && (
            <button className="reporting-new-btn" onClick={() => openBuilder()}>
              + {t("reportingPage.newReport")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="reporting-loading">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div className="reporting-empty">
            {t("reportingPage.noReportsYet")}{" "}
            {isAnalyst && (
              <span
                className="reporting-link"
                onClick={() => openBuilder()}
                role="button"
                tabIndex={0}
              >
                {t("reportingPage.createFirstReport")}
              </span>
            )}
          </div>
        ) : (
          <>
            <table className="reporting-table">
              <thead>
                <tr>
                  <th className="rt-check">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>{t("reportingPage.columnReportName")}</th>
                  <th>{t("reportingPage.columnStatus")}</th>
                  <th>{t("reportingPage.columnLastUpdated")}</th>
                  <th className="rt-actions" />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.key}>
                    <td className="rt-check">
                      <input
                        type="checkbox"
                        checked={selected.has(row.key)}
                        onChange={() => toggleSelect(row.key)}
                      />
                    </td>
                    <td
                      className="rt-name"
                      onClick={() => openBuilder(row)}
                      role="button"
                    >
                      {row.name}
                    </td>
                    <td>
                      <span className={`rt-badge rt-badge--${row.status}`}>
                        <span className="rt-dot" />
                        {t(`common.status.${row.status}`)}
                      </span>
                    </td>
                    <td className="rt-date">
                      {dayjs(row.updatedAt).format("MMMM D, YYYY")}
                    </td>
                    <td className="rt-actions">
                      {isAnalyst && (
                        <button
                          className="rt-icon-btn"
                          title={t("reportingPage.edit")}
                          onClick={() => openBuilder(row)}
                        >
                          <PencilIcon />
                        </button>
                      )}
                      <button
                        className="rt-icon-btn"
                        title={t("reportingPage.download")}
                        disabled={row.kind !== "report"}
                        onClick={() =>
                          gate.requirePaid(
                            () => handleExport(row),
                            t("reportingPage.exportingTheReport"),
                          )
                        }
                      >
                        <DownloadIcon />
                      </button>
                      {isAdmin && (
                        <button
                          className="rt-icon-btn rt-icon-btn--danger"
                          title={t("reportingPage.delete")}
                          onClick={() => handleDelete(row)}
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="reporting-pagination">
                <button
                  className="rp-arrow"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`rp-page ${p === page ? "rp-page--active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="rp-arrow"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportingPage;
